import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const Shop = () => {
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [showCheckoutForm, setShowCheckoutForm] = useState(false);
  const [checkoutStatus, setCheckoutStatus] = useState('');
  const [searchParams] = useSearchParams();
  const [customerDetails, setCustomerDetails] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    postcode: '',
    country: 'Australia',
    orderNotes: ''
  });

  // Check for successful payment
  const success = searchParams.get('success');
  const cancelled = searchParams.get('canceled');

  const products = [
    {
      id: 1,
      name: 'Original Flavour Biltong',
      weight: '50g',
      description: '100% Australian beef, air-dried using traditional methods.',
      price: 8.95,
      image: 'https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/jlwf1bqr_OB%20Original%20-%20Signle%20pack%20.jpg',
      protein: '52%',
      fat: '<5%',
      badge: 'Original',
      sku: 'OB-ORIG-50G'
    },
    {
      id: 2,
      name: 'Chilli Flavour Biltong',
      weight: '50g',
      description: 'Traditional recipe enhanced with a balanced chilli spice blend.',
      price: 8.95,
      image: 'https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/ckmui9ly_Chilli%20-%20Single%20pack%20.jpg',
      protein: '52%',
      fat: '<5%',
      badge: 'Spicy',
      sku: 'OB-CHIL-50G'
    },
    {
      id: 3,
      name: 'Starter Pack',
      weight: '300g total',
      description: '3x Original + 3x Chilli bags. Perfect introduction to our range.',
      price: 59.95,
      originalPrice: 53.70,
      image: 'https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/uwztf879_6%20pack%20Mixed%20.jpg',
      protein: '52%',
      fat: '<5%',
      badge: 'Bundle',
      sku: 'OB-STARTER-6',
      freeShipping: true,
      pieces: '6 bags'
    },
    {
      id: 4,
      name: 'Fan Pack',
      weight: '500g total',
      description: 'Best value! Mix of 10 bags. Choose your favourite flavours.',
      price: 89.50,
      originalPrice: 89.50,
      image: 'https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/44lqi9el_Multi%20pack%20colours%20.jpg',
      protein: '52%',
      fat: '<5%',
      badge: 'Best Value',
      sku: 'OB-FAN-10',
      freeShipping: true,
      pieces: '10 bags'
    }
  ];

  const addToCart = (product) => {
    const existingItem = cart.find(item => item.id === product.id);
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity === 0) {
      removeFromCart(productId);
    } else {
      setCart(cart.map(item =>
        item.id === productId
          ? { ...item, quantity: newQuantity }
          : item
      ));
    }
  };

  const cartTotal = cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  const cartItemCount = cart.reduce((count, item) => count + item.quantity, 0);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCustomerDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProceedToCheckout = () => {
    setShowCart(false);
    setShowCheckoutForm(true);
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    setCheckoutStatus('processing');
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Prepare checkout data
      const checkoutData = {
        items: cart.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        customer_email: customerDetails.email,
        customer_name: `${customerDetails.firstName} ${customerDetails.lastName}`,
        shipping_address: {
          line1: customerDetails.address,
          city: customerDetails.city,
          state: customerDetails.state,
          postal_code: customerDetails.postcode,
          country: customerDetails.country
        }
      };
      
      // Create Stripe checkout session
      const response = await fetch(`${backendUrl}/api/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(checkoutData)
      });

      const data = await response.json();
      
      if (data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = data.checkout_url;
      } else {
        setCheckoutStatus('error');
        setTimeout(() => setCheckoutStatus(''), 3000);
      }
    } catch (error) {
      console.error('Checkout error:', error);
      setCheckoutStatus('error');
      setTimeout(() => setCheckoutStatus(''), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Buy Biltong Online"
        description="Shop premium Australian biltong online. 52% protein, 100% Australian beef. Original and Chilli flavours. Free shipping over $59. Secure checkout with Stripe."
        keywords="buy biltong online, biltong shop, Australian biltong online, order biltong, biltong delivery Australia, protein snacks online"
        url="/shop"
        image="https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/jlwf1bqr_OB%20Original%20-%20Signle%20pack%20.jpg"
      />
      <Header />

      {/* Cart Button - Fixed */}
      <button 
        onClick={() => setShowCart(true)}
        data-testid="cart-button"
        className="fixed top-24 right-4 md:right-8 bg-red-700 text-white px-4 py-2 rounded-md hover:bg-red-800 transition-colors z-40 font-medium shadow-lg flex items-center gap-2"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
        <span>{cartItemCount}</span>
      </button>

      {/* Hero Section - Matching Home.jsx style */}
      <section className="pt-20 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-4 text-sm tracking-[0.3em] text-gray-600 font-semibold">
            PREMIUM COLLECTION
          </div>
          <h1 className="text-5xl md:text-7xl font-bold mb-4">
            <span className="text-red-700">SHOP</span>
            <br />
            <span className="text-gray-900">BILTONG</span>
          </h1>
          <p className="text-lg text-gray-700 mb-6 max-w-2xl mx-auto">
            Premium Australian beef. 52% protein. 200 years of South African heritage.
          </p>

          {/* Success/Cancel Messages */}
          {success && (
            <div className="bg-green-50 border border-green-500 text-green-800 px-6 py-4 rounded-lg mb-6 max-w-2xl mx-auto" data-testid="success-message">
              <h3 className="font-bold text-lg mb-2">Payment Successful!</h3>
              <p>Thank you for your order! Check your email for confirmation.</p>
            </div>
          )}
          
          {cancelled && (
            <div className="bg-yellow-50 border border-yellow-500 text-yellow-800 px-6 py-4 rounded-lg mb-6 max-w-2xl mx-auto" data-testid="cancelled-message">
              <h3 className="font-bold text-lg mb-2">Order Cancelled</h3>
              <p>Your cart is saved. Continue shopping when ready.</p>
            </div>
          )}

          {/* Free Shipping Banner */}
          <div className="inline-block bg-red-700 text-white px-8 py-3 rounded-full font-medium">
            FREE SHIPPING ON ORDERS OVER $59
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          
          {/* Products Grid - 2 columns layout like Home.jsx */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            {products.map(product => (
              <div 
                key={product.id} 
                className="bg-gray-50 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col"
                data-testid={`product-card-${product.id}`}
              >
                {/* Product Image */}
                <div className="aspect-square overflow-hidden relative">
                  {product.freeShipping && (
                    <div className="absolute top-4 left-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold z-10">
                      Free Shipping
                    </div>
                  )}
                  <div className={`absolute top-4 right-4 px-3 py-1 rounded-full text-sm font-semibold z-10 ${
                    product.badge === 'Original' ? 'bg-amber-700 text-white' : 
                    product.badge === 'Spicy' ? 'bg-red-600 text-white' :
                    product.badge === 'Bundle' ? 'bg-green-700 text-white' :
                    'bg-purple-700 text-white'
                  }`}>
                    {product.badge}
                  </div>
                  <img 
                    src={product.image} 
                    alt={product.name}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Product Info */}
                <div className="p-6 flex flex-col">
                  <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
                  <p className="text-gray-600 mb-4">{product.description}</p>
                  
                  {/* Stats Row */}
                  <div className="flex justify-between px-4 mb-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-700">{product.protein}</div>
                      <div className="text-sm text-gray-600">Protein</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-700">{product.fat}</div>
                      <div className="text-sm text-gray-600">Fat</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-700">{product.weight}</div>
                      <div className="text-sm text-gray-600">Weight</div>
                    </div>
                    {product.pieces && (
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-700">{product.pieces}</div>
                        <div className="text-sm text-gray-600">Included</div>
                      </div>
                    )}
                  </div>

                  {/* Price and Button */}
                  <div className="flex items-center justify-between gap-4 mt-auto">
                    <div>
                      <span className="text-3xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                      <span className="text-sm text-gray-500 ml-1">inc. GST</span>
                    </div>
                    <button 
                      onClick={() => addToCart(product)}
                      data-testid={`add-to-cart-${product.id}`}
                      className="bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors font-medium"
                    >
                      Add to Cart
                    </button>
                  </div>
                  
                  <p className="text-xs text-gray-400 mt-3">SKU: {product.sku}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Australian Made Certification */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-center gap-12">
            {/* Logo with proper clear space (3x descriptor height as per guidelines) */}
            <a 
              href="https://australianmade.com.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex-shrink-0 p-6"
              title="Australian Made"
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_185b30bf-6e94-434b-8e3e-3ca910668034/artifacts/lq4m5jia_AMCL%20Logo.jpg" 
                alt="Australian Made Logo" 
                className="h-32 w-auto mix-blend-multiply"
              />
            </a>
            <div className="text-center md:text-left max-w-xl">
              <h3 className="text-2xl font-bold mb-3">Proudly Australian Made</h3>
              <p className="text-gray-600">
                Original Bull is the first and only biltong producer to hold official Australian Made certification. 
                100% Australian beef, crafted in Queensland using traditional methods.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Info Cards - Matching Home.jsx style */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-red-700 mb-2">52%</div>
              <div className="font-bold mb-1">PROTEIN</div>
              <div className="text-sm text-gray-600">2X Steak Power</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-red-700 mb-2">100%</div>
              <div className="font-bold mb-1">NATURAL</div>
              <div className="text-sm text-gray-600">No Preservatives</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-red-700 mb-2">$59+</div>
              <div className="font-bold mb-1">FREE SHIP</div>
              <div className="text-sm text-gray-600">Australia-Wide</div>
            </div>
            <div className="text-center p-6 bg-gray-50 rounded-xl">
              <div className="text-3xl font-bold text-red-700 mb-2">200</div>
              <div className="font-bold mb-1">YEARS</div>
              <div className="text-sm text-gray-600">Heritage Recipe</div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Safety Information */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Shipping Info */}
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <h3 className="font-bold text-xl mb-4 text-gray-900">Shipping Information</h3>
              <div className="space-y-3 text-gray-700">
                <p><strong>Australia-Wide:</strong> $8.95 flat rate</p>
                <p><strong>FREE over $59:</strong> No shipping charge</p>
                <p><strong>Processing:</strong> Ships in 1-2 business days</p>
                <p><strong>Delivery:</strong> 3-5 business days via Australia Post</p>
              </div>
            </div>

            {/* Food Safety */}
            <div className="bg-white p-8 rounded-2xl shadow-sm">
              <h3 className="font-bold text-xl mb-4 text-gray-900">Product Information</h3>
              <div className="space-y-3 text-gray-700 text-sm">
                <p><strong>Allergens:</strong> Contains beef. May contain traces of soy.</p>
                <p><strong>Storage:</strong> Cool, dry place. Refrigerate after opening.</p>
                <p><strong>Shelf Life:</strong> 12 months unopened</p>
                <p><strong>Returns:</strong> Unopened products only (within 7 days)</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Secure Checkout Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-white border-t">
        <div className="max-w-7xl mx-auto text-center">
          <h3 className="text-2xl font-bold mb-6">Secure Checkout</h3>
          <p className="text-gray-600 mb-6">All major payment methods accepted. Secure payments powered by Stripe.</p>
          <div className="flex flex-wrap justify-center gap-4 text-gray-500 text-sm">
            <span>Visa</span>
            <span>•</span>
            <span>Mastercard</span>
            <span>•</span>
            <span>Amex</span>
            <span>•</span>
            <span>Apple Pay</span>
            <span>•</span>
            <span>Google Pay</span>
          </div>
        </div>
      </section>

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowCart(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Your Cart</h2>
                <button onClick={() => setShowCart(false)} className="text-gray-500 hover:text-gray-700 text-2xl" data-testid="close-cart">×</button>
              </div>

              {cart.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <p className="text-gray-600">Your cart is empty</p>
                </div>
              ) : (
                <>
                  <div className="space-y-4 mb-6">
                    {cart.map(item => (
                      <div key={item.id} className="flex gap-4 items-center border-b pb-4" data-testid={`cart-item-${item.id}`}>
                        <img src={item.image} alt={item.name} className="w-20 h-20 object-cover rounded" />
                        <div className="flex-grow">
                          <h3 className="font-bold">{item.name}</h3>
                          <p className="text-gray-600 text-sm">${item.price.toFixed(2)} each</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded hover:bg-gray-200"
                            data-testid={`decrease-qty-${item.id}`}
                          >
                            -
                          </button>
                          <span className="font-bold w-8 text-center">{item.quantity}</span>
                          <button 
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded hover:bg-gray-200"
                            data-testid={`increase-qty-${item.id}`}
                          >
                            +
                          </button>
                        </div>
                        <button 
                          onClick={() => removeFromCart(item.id)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          data-testid={`remove-item-${item.id}`}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4 mb-6">
                    <div className="flex justify-between text-lg mb-2">
                      <span>Subtotal:</span>
                      <span>${cartTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg mb-2">
                      <span>Shipping:</span>
                      <span className={cartTotal >= 59 ? 'text-green-600 font-medium' : ''}>{cartTotal >= 59 ? 'FREE' : '$8.95'}</span>
                    </div>
                    <div className="flex justify-between text-xl font-bold border-t pt-2">
                      <span>Total:</span>
                      <span className="text-red-700">${(cartTotal + (cartTotal >= 59 ? 0 : 8.95)).toFixed(2)}</span>
                    </div>
                    {cartTotal < 59 && (
                      <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded mt-3">
                        Add <span className="font-bold">${(59 - cartTotal).toFixed(2)}</span> more for FREE shipping
                      </p>
                    )}
                    {cartTotal >= 59 && (
                      <p className="text-sm text-green-700 font-medium bg-green-50 p-3 rounded mt-3">
                        Free shipping applied!
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <button 
                      onClick={handleProceedToCheckout}
                      data-testid="proceed-to-checkout"
                      className="w-full bg-red-700 text-white px-6 py-3 rounded-md hover:bg-red-800 transition-colors font-medium"
                    >
                      Proceed to Checkout
                    </button>
                    
                    <button 
                      onClick={() => setShowCart(false)}
                      className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:border-red-700 hover:text-red-700 transition-colors font-medium"
                    >
                      Continue Shopping
                    </button>
                  </div>

                  <p className="text-xs text-center text-gray-500 mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Checkout Form Modal */}
      {showCheckoutForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowCheckoutForm(false)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Checkout</h2>
                <button onClick={() => setShowCheckoutForm(false)} className="text-gray-500 hover:text-gray-700 text-2xl" data-testid="close-checkout">×</button>
              </div>

              {/* Order Summary */}
              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="font-bold text-lg mb-3">Order Summary</h3>
                {cart.map(item => (
                  <div key={item.id} className="flex justify-between text-sm mb-2">
                    <span>{item.name} × {item.quantity}</span>
                    <span>${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t mt-3 pt-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span>Subtotal:</span>
                    <span>${cartTotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-2">
                    <span>Shipping:</span>
                    <span className={cartTotal >= 59 ? 'text-green-600' : ''}>{cartTotal >= 59 ? 'FREE' : '$8.95'}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-red-700">${(cartTotal + (cartTotal >= 59 ? 0 : 8.95)).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Customer Details Form */}
              <form onSubmit={handleCheckout}>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-bold text-lg mb-3">Contact Information</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1">First Name *</label>
                        <input
                          type="text"
                          name="firstName"
                          value={customerDetails.firstName}
                          onChange={handleInputChange}
                          required
                          data-testid="checkout-firstname"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Last Name *</label>
                        <input
                          type="text"
                          name="lastName"
                          value={customerDetails.lastName}
                          onChange={handleInputChange}
                          required
                          data-testid="checkout-lastname"
                          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={customerDetails.email}
                      onChange={handleInputChange}
                      required
                      data-testid="checkout-email"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={customerDetails.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+61 4XX XXX XXX"
                      data-testid="checkout-phone"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <h3 className="font-bold text-lg mb-3 mt-6">Shipping Address</h3>
                    <div>
                      <label className="block text-sm font-medium mb-1">Street Address *</label>
                      <input
                        type="text"
                        name="address"
                        value={customerDetails.address}
                        onChange={handleInputChange}
                        required
                        placeholder="123 Main Street"
                        data-testid="checkout-address"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">City *</label>
                      <input
                        type="text"
                        name="city"
                        value={customerDetails.city}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-city"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">State *</label>
                      <select
                        name="state"
                        value={customerDetails.state}
                        onChange={handleInputChange}
                        required
                        data-testid="checkout-state"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      >
                        <option value="">Select State</option>
                        <option value="NSW">NSW</option>
                        <option value="VIC">VIC</option>
                        <option value="QLD">QLD</option>
                        <option value="WA">WA</option>
                        <option value="SA">SA</option>
                        <option value="TAS">TAS</option>
                        <option value="ACT">ACT</option>
                        <option value="NT">NT</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Postcode *</label>
                      <input
                        type="text"
                        name="postcode"
                        value={customerDetails.postcode}
                        onChange={handleInputChange}
                        required
                        placeholder="2000"
                        data-testid="checkout-postcode"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Order Notes (Optional)</label>
                    <textarea
                      name="orderNotes"
                      value={customerDetails.orderNotes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Any special delivery instructions..."
                      data-testid="checkout-notes"
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-700 focus:border-transparent"
                    />
                  </div>

                  <div className="space-y-3 pt-4">
                    <button 
                      type="submit"
                      disabled={checkoutStatus === 'processing'}
                      data-testid="place-order-btn"
                      className={`w-full px-6 py-3 rounded-md transition-colors font-medium ${
                        checkoutStatus === 'processing' 
                          ? 'bg-gray-400 cursor-not-allowed text-white' 
                          : 'bg-red-700 text-white hover:bg-red-800'
                      }`}
                    >
                      {checkoutStatus === 'processing' ? 'Processing...' : 'Continue to Payment'}
                    </button>
                    
                    {checkoutStatus === 'success' && (
                      <div className="bg-green-50 border border-green-400 text-green-700 px-4 py-3 rounded">
                        Redirecting to secure payment...
                      </div>
                    )}
                    
                    {checkoutStatus === 'error' && (
                      <div className="bg-red-50 border border-red-400 text-red-700 px-4 py-3 rounded">
                        Error processing order. Please try again.
                      </div>
                    )}
                    
                    <button 
                      type="button"
                      onClick={() => {
                        setShowCheckoutForm(false);
                        setShowCart(true);
                      }}
                      data-testid="back-to-cart-btn"
                      className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-md hover:border-red-700 hover:text-red-700 transition-colors font-medium"
                    >
                      Back to Cart
                    </button>
                  </div>
                </div>
              </form>

              <p className="text-xs text-center text-gray-500 mt-4">
                You'll be redirected to Stripe's secure checkout to complete your payment.
              </p>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Shop;
